import {
  ORDER_COMMENT_COLUMN,
  ORDER_EXECUTION_DETAIL_COLUMNS,
  ORDER_GRID_COLUMNS,
  formatDateDisplay,
  formatQuantityTons,
  formatTextDisplay,
  formatWindowDisplay,
  resolveOrderTimeFieldValue,
  resolveReadonlyTextCell,
} from "../hooks/useDataManagerOrdersUtils.js";

export const ORDER_FILTERS_PREFERENCE_KEY = "orders_filters";
export const ORDER_PDF_PREFERENCE_KEY = "orders_pdf";

export const ORDER_DEFAULT_ADVANCED_FILTER_KEYS = Object.freeze([
  "delivery_date",
  "loading_client_name",
  "is_dompeur",
  "ontario",
]);

const ORDER_GRID_COLUMN_BY_KEY = new Map(
  [...ORDER_GRID_COLUMNS, ...ORDER_EXECUTION_DETAIL_COLUMNS, ORDER_COMMENT_COLUMN]
    .map((column) => [column.key, column]),
);

export const ORDER_ADVANCED_FILTER_CATALOG = Object.freeze([
  { key: "delivery_date", label: "Période livraison", type: "date-range" },
  { key: "delivery_period", label: "Jour / Soir", type: "enum", options: [
    { value: "day", label: "Jour" },
    { value: "evening", label: "Soir" },
  ] },
  { key: "loading_client_name", label: "Client chargement", type: "loading-client" },
  { key: "coop", label: "Facturer à", type: "text" },
  { key: "is_dompeur", label: "Dompeur", type: "boolean" },
  { key: "ontario", label: "Ontario", type: "boolean" },
  { key: "order_id", label: "Bon commande", type: "text" },
  { key: "work_order_number", label: "Bon travail", type: "text" },
  { key: "client_name", label: "Client déchargement", type: "loading-client" },
  { key: "client_code", label: "# client", type: "text" },
  { key: "building", label: "Bâtiment", type: "text" },
  { key: "loading_silo", label: "Silo", type: "text" },
  { key: "grain_code", label: "Code produit", type: "text" },
  { key: "grain_type", label: "Produit", type: "text" },
  { key: "address", label: "Adresse déchargement", type: "text" },
  { key: "comment", label: "Commentaire", type: "text" },
  { key: "aspirator", label: "Aspirateur", type: "boolean" },
  { key: "is_preloaded_previous_day", label: "Chargé la veille", type: "boolean" },
  { key: "loading_priority", label: "Priorité chargement", type: "enum", options: [
    { value: "P1", label: "P1" },
    { value: "P2", label: "P2" },
    { value: "P3", label: "P3" },
  ] },
  { key: "delivery_priority", label: "Priorité déchargement", type: "enum", options: [
    { value: "P1", label: "P1" },
    { value: "P2", label: "P2" },
    { value: "P3", label: "P3" },
  ] },
  { key: "quantity_tons", label: "Nb tm", type: "number-range" },
  { key: "actual_loaded_quantity_tons", label: "Chargé réel t", type: "number-range" },
  { key: "actual_delivered_quantity_tons", label: "Livré réel t", type: "number-range" },
  { key: "delivered_by_driver_name", label: "Livré par", type: "text" },
]);

export const ORDER_ADVANCED_FILTER_CATALOG_BY_KEY = Object.freeze(
  Object.fromEntries(ORDER_ADVANCED_FILTER_CATALOG.map((filter) => [filter.key, filter])),
);

export const ORDER_PDF_DEFAULT_COLUMN_KEYS = Object.freeze([
  "delivery_date",
  "order_id",
  "work_order_number",
  "client_name",
  "building",
  "loading_silo",
  "grain_code",
  "quantity_tons",
]);

export const ORDER_PDF_COLUMN_CATALOG = Object.freeze([
  { key: "delivery_date", label: "Date" },
  { key: "order_id", label: "No de commande" },
  { key: "work_order_number", label: "Bon de travail" },
  { key: "client_name", label: "Client livré" },
  { key: "building", label: "BAT" },
  { key: "loading_silo", label: "Silo" },
  { key: "grain_code", label: "No Produit" },
  { key: "quantity_tons", label: "Quantité" },
  ...ORDER_GRID_COLUMNS
    .filter((column) => (
      !ORDER_PDF_DEFAULT_COLUMN_KEYS.includes(column.key)
      && column.key !== "end_of_day_load_action"
    ))
    .map((column) => ({ key: column.key, label: column.label })),
  ...ORDER_EXECUTION_DETAIL_COLUMNS
    .filter((column) => !ORDER_GRID_COLUMNS.some((gridColumn) => gridColumn.key === column.key))
    .map((column) => ({ key: column.key, label: column.label })),
  { key: ORDER_COMMENT_COLUMN.key, label: ORDER_COMMENT_COLUMN.label },
]);

export const ORDER_PDF_COLUMN_CATALOG_BY_KEY = Object.freeze(
  Object.fromEntries(ORDER_PDF_COLUMN_CATALOG.map((column) => [column.key, column])),
);

function normalizeKeyList(candidateKeys, allowedKeys, fallbackKeys) {
  if (!Array.isArray(candidateKeys)) {
    return [...fallbackKeys];
  }

  const allowed = new Set(allowedKeys);
  const seen = new Set();
  const keys = candidateKeys.filter((key) => {
    if (!allowed.has(key) || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });

  return keys;
}

export function resolveVisibleOrderFilterKeys(savedPrefs = null) {
  if (savedPrefs && Array.isArray(savedPrefs.column_order)) {
    return normalizeKeyList(
      savedPrefs.column_order,
      ORDER_ADVANCED_FILTER_CATALOG.map((filter) => filter.key),
      [],
    );
  }

  return [...ORDER_DEFAULT_ADVANCED_FILTER_KEYS];
}

export function resolveOrderPdfColumnKeys(savedPrefs = null) {
  if (savedPrefs && Array.isArray(savedPrefs.column_order) && savedPrefs.column_order.length > 0) {
    const keys = normalizeKeyList(
      savedPrefs.column_order,
      ORDER_PDF_COLUMN_CATALOG.map((column) => column.key),
      [],
    );
    if (keys.length > 0) {
      return keys;
    }
  }

  return [...ORDER_PDF_DEFAULT_COLUMN_KEYS];
}

export function resolveOrderPdfColumnLabels(savedPrefs = null) {
  const savedLabels = savedPrefs?.column_labels && typeof savedPrefs.column_labels === "object"
    ? savedPrefs.column_labels
    : {};

  return Object.fromEntries(
    ORDER_PDF_COLUMN_CATALOG.map((column) => [
      column.key,
      typeof savedLabels[column.key] === "string" && savedLabels[column.key].trim()
        ? savedLabels[column.key].trim()
        : column.label,
    ]),
  );
}

export function normalizeOrderPdfColumnLabels(labels = {}) {
  return Object.fromEntries(
    ORDER_PDF_COLUMN_CATALOG.map((column) => {
      const candidate = typeof labels?.[column.key] === "string" ? labels[column.key].trim() : "";
      return [column.key, candidate || column.label];
    }),
  );
}

export function buildPersistedOrderPdfColumnLabels(labels = {}) {
  return Object.fromEntries(
    ORDER_PDF_COLUMN_CATALOG.flatMap((column) => {
      const candidate = typeof labels?.[column.key] === "string" ? labels[column.key].trim() : "";
      return candidate && candidate !== column.label ? [[column.key, candidate]] : [];
    }),
  );
}

export function buildOrderPdfExportColumns(selectedKeys = [], labels = {}) {
  const normalizedLabels = normalizeOrderPdfColumnLabels(labels);
  const keys = normalizeKeyList(
    selectedKeys,
    ORDER_PDF_COLUMN_CATALOG.map((column) => column.key),
    ORDER_PDF_DEFAULT_COLUMN_KEYS,
  );
  const resolvedKeys = keys.length > 0 ? keys : [...ORDER_PDF_DEFAULT_COLUMN_KEYS];

  return resolvedKeys.map((key) => {
    const catalogColumn = ORDER_PDF_COLUMN_CATALOG_BY_KEY[key];
    const gridColumn = ORDER_GRID_COLUMN_BY_KEY.get(key);
    return {
      key,
      label: normalizedLabels[key] || catalogColumn?.label || key,
      widthHint: Number(gridColumn?.defaultWidth) || null,
    };
  });
}

export function getOrderReportFieldDisplayValue(order, fieldKey) {
  if (!order || !fieldKey) {
    return "";
  }

  if (fieldKey === "delivery_date") {
    const display = formatDateDisplay(order[fieldKey]);
    return display === "—" ? "" : display;
  }

  if (fieldKey === "quantity_tons") {
    const value = order[fieldKey];
    return value === null || value === undefined || value === "" ? "" : formatQuantityTons(value);
  }

  if (fieldKey === "delivery_period") {
    return order[fieldKey] === "evening" ? "Soir" : "Jour";
  }

  if (fieldKey === "loading_window_start" || fieldKey === "delivery_window_start") {
    const display = formatWindowDisplay(resolveOrderTimeFieldValue(order, fieldKey));
    return display === "—" ? "" : display;
  }

  const column = ORDER_GRID_COLUMN_BY_KEY.get(fieldKey) || { key: fieldKey };
  const display = resolveReadonlyTextCell(order, column).display;
  return display === "—" ? "" : formatTextDisplay(display);
}
